import React, { useEffect, useMemo } from "react";
import "../../../index.css";
import { pivotiseData } from "../Utils/DataTransform/transformData2";
import { useDispatch, useSelector } from "react-redux";
import { sortData } from "../Utils/SortData";
import {
  getL1SheetProperty,
  getL2SheetProperty,
  getWorkbookProperty,
  setOffset,
} from "../../../features/reduxworkbook/WorkbookSlice";
import DynamicTable from "../Utils/DynamicTable";
import { checkExternalTotalRequired } from "../Utils/DataTransform/checkExternalTotals";
import { useFetchTotalsFromApi } from "../Utils/DataTransform/getTotalsFromApi";
import { updateTableHeaderWithHirarchyKeys } from "../Utils/updateTableHeaderWithHirarchyKeys";
import LoadingIndicator from "../../BasicComponents/LoadingIndicator";
import { useState } from "react";
import { MdNavigateNext } from "react-icons/md";
import { MdNavigateBefore } from "react-icons/md";
const Table = (props) => {
  const {
    Data,
    groupByKeys: rawGroupByKeys,
    valueKeys,
    Sheetid,
    sortProperties,
    dataSourceID,
    filters,
    commonFilters,
    vizType,
    rowLimit,
    offset,
  } = props;
  const dispatch = useDispatch();
  const SufixPrefix = valueKeys.map((item) => {
    return {
      key: item.responseKey,
      numberObject: item.numberFormat, // Use square brackets for dynamic keys
    };
  });
  const rowLimitValue = rowLimit ?? 25;
  const offsetValue = offset ?? 0;
  const ruduxPage = offsetValue / rowLimitValue;
  const [currentPage, setCurrentPage] = useState(ruduxPage ?? 0);
  const rowsPerPage = 25;

  useEffect(() => {
    const offsetValue = currentPage * rowLimitValue;
    dispatch(setOffset({ limit: 25, vizType, Sheetid, offset: offsetValue }));
  }, [currentPage]);

  const handleNext = () => {
    if (Data.length === rowLimitValue) {
      setCurrentPage((prev) => prev + 1);
    }
  };
  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );
  const quickTableArray = useSelector((state) => {
    return getL2SheetProperty(state, Sheetid, "actions", "quickTable");
  });
  const hierarchy =
    useSelector((state) => getWorkbookProperty(state, "hierarchy")) || [];
  const {
    isRowTotalVisible,
    isColumnTotalVisible,
    isMeasureOnTop,
    conditionalFormatting,
    isCustomAlign,
    rowAlign,
    valueAlign,
  } = customizationObject;

  const externalTotalResult = useMemo(() => {
    return checkExternalTotalRequired({
      valueKeys,
      quickTableArray,
      sortProperties,
      isRowTotalVisible,
      isColumnTotalVisible,
      Data,
      dataSourceID,
      rawRowKeys: rawGroupByKeys,
      rawGroupByKeys: [],
      Sheetid,
      filters,
      commonFilters,
    });
  }, [
    valueKeys,
    quickTableArray,
    sortProperties,
    isRowTotalVisible,
    isColumnTotalVisible,
    Data,
    dataSourceID,
    rawGroupByKeys,
  ]);
  const {
    grandTotalRequired,
    columnTotalRequired,
    rowTotalRequired,
    rowTotalQuery,
    columnTotalQuery,
    grandTotalQuery,
  } = externalTotalResult;

  // Fetch total data from API
  const {
    data: totalData,
    isLoading = true,
    isSuccess,
    isError,
    error,
  } = useFetchTotalsFromApi({
    grandTotalRequired,
    columnTotalRequired,
    rowTotalRequired,
    rowTotalQuery,
    columnTotalQuery,
    grandTotalQuery,
    Sheetid,
  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isError) {
    console.error(error);
    return <p>Error while fetching totals : {error?.data?.error}</p>;
  }
  if (isSuccess) {
    const {
      uniqueTableRows,
      measureAttachedColumnValueKeys,
      totalRowForColumnTotals,
      destructuredHeader,
      headerKeySequence,
      rowTotalColumnKeys,
      maxMinData,
    } = pivotiseData({
      rawData: Data,
      rawGroupByKeys: [],
      rawRowKeys: rawGroupByKeys,
      valueObjectArray: valueKeys,
      isRowTotalVisible: isRowTotalVisible,
      isColumnTotalVisible: isColumnTotalVisible,
      isMeasureOnTop: isMeasureOnTop !== undefined ? isMeasureOnTop : false,
      totalData: totalData,
      grandTotalRequired: grandTotalRequired,
      columnTotalRequired: columnTotalRequired,
      rowTotalRequired: rowTotalRequired,
      quickTableArray: quickTableArray,
    });

    const data =
      sortProperties.length > 0 && rawGroupByKeys.length > 0
        ? sortData({
            transformedData: uniqueTableRows,
            sortProperties,
            rawRowKeys: rawGroupByKeys,
            rawGroupByKeys: [],
            requiredArray: rawGroupByKeys,
          })
        : uniqueTableRows;

    isColumnTotalVisible && data.push(totalRowForColumnTotals);

    const headerHierarchyKeys = [];
    rawGroupByKeys.forEach((element) => {
      let hierarchyKey = null;
      if (element.dataType === "String") {
        hierarchyKey = element.rename;
      }
      if (element.dataType === "Date") {
        hierarchyKey = `${element.rename}${element.dateFormat.value}`;
      }
      headerHierarchyKeys.push(hierarchyKey);
    });

    const mappedHierarchy = headerHierarchyKeys.reduce((acc, hItemKey) => {
      const matchingItem = hierarchy.find((hItem) =>
        hItem.hierarchyArray.some((hItemHItem) => {
          if (hItemHItem.dataType === "String") {
            return hItemHItem.rename === hItemKey;
          } else if (hItemHItem.dataType === "Date") {
            return (
              `${hItemHItem.rename}${hItemHItem.dateFormat.value}` === hItemKey
            );
          }
        })
      );

      if (matchingItem) {
        acc[hItemKey] = matchingItem; // Map headerHierarchy key to the matching item
      }

      return acc;
    }, {});
    const transformedTableHeader = updateTableHeaderWithHirarchyKeys({
      destructuredHeader,
      rawRowKeys: rawGroupByKeys,
      rawGroupByKeys: [],
      isMeasureOnTop: isMeasureOnTop !== undefined ? isMeasureOnTop : false,
      headerHierarchyKeys,
      mappedHierarchy,
      isColumnTotalVisible,
      vizType
    });

    return (
      <>
        <DynamicTable
          tableHeader={transformedTableHeader}
          tableData={data}
          headerKeys={headerKeySequence}
          quickTable={quickTableArray.length > 0 && quickTableArray}
          measureAttachedColumnValueKeys={measureAttachedColumnValueKeys}
          SufixPrefix={SufixPrefix}
          Sheetid={Sheetid}
          rowKeys={rawGroupByKeys}
          valueKeys={valueKeys}
          isMeasureOnTop={isMeasureOnTop !== undefined ? isMeasureOnTop : false}
          rowTotalColumnKeys={rowTotalColumnKeys}
          isColumnPresent={false}
          groupByKeys={[]}
          conditionalFormatting={conditionalFormatting}
          maxMinData={maxMinData}
          hierarchy={hierarchy}
          isRowTotalVisible={isRowTotalVisible}
          isColumnTotalVisible={isColumnTotalVisible}
          isCustomAlign={isCustomAlign}
          rowAlign={rowAlign}
          valueAlign={valueAlign}
          columnTotal={totalRowForColumnTotals}
          grandTotalAccordingToKey={totalData.columnTotals}
          vizType={vizType}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
        />
        {/* Pagination Controls */}
        {Data.length === rowsPerPage ? (
          <div className="flex justify-end items-center w-full ">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              className={`px-2 gap-x-1 py-[1.7px] border ${
                currentPage === 0
                  ? "bg-gray-200 text-black"
                  : "bg-blue-950 text-white"
              } rounded disabled:opacity-75 tracking-wide flex items-center justify-center`}
            >
              <MdNavigateBefore /> Prev
            </button>

            <span className="px-4">{currentPage}</span>

            <button
              onClick={handleNext}
              disabled={Data.length < rowLimitValue}
              className={`px-2 gap-x-1 py-[1.7px] border ${
                Data.length < rowLimitValue
                  ? "bg-gray-200 text-black"
                  : "bg-blue-950 text-white"
              } rounded disabled:opacity-75 tracking-wide flex items-center justify-center`}
            >
              Next <MdNavigateNext />
            </button>
          </div>
        ) : null}
      </>
    );
  }
};

export default Table;
